import React from 'react';
import { Link } from 'react-router-dom';
import ImplantCenterLayout from './ImplantCenterLayout';

const ImplantCosmeticDentistry = () => {
  const services = [
    {
      title: 'Teeth Whitening',
      description: 'Professional whitening treatments that brighten your smile up to 10 shades. In-office and take-home options available.',
      image: '/images/services/whitening-new-1.jpg'
    },
    {
      title: 'Porcelain Veneers',
      description: 'Custom-crafted thin shells that cover the front of teeth, correcting chips, stains, gaps, and misalignment.',
      image: '/images/services/veneers-new-1.jpg'
    },
    {
      title: 'Invisalign® Clear Aligners',
      description: 'Straighten your teeth discreetly with clear, removable aligners. No metal brackets or wires.',
      image: '/images/services/frame-1288-2.png'
    },
    {
      title: 'CEREC Same-Day Crowns',
      description: 'Advanced CAD/CAM technology creates custom ceramic crowns in a single visit. No temporary crowns needed.',
      image: '/images/services/crowns-new-1.jpg'
    },
    {
      title: 'First Fit Veneers',
      description: 'Revolutionary digital veneers technology delivers a brand-new smile in as little as two hours.',
      image: '/images/services/veneers-hero.jpg'
    },
    {
      title: 'Smile Makeover',
      description: 'Comprehensive treatment combining multiple cosmetic procedures for a complete smile transformation.',
      image: '/images/services/dental-implant-hero.jpg'
    }
  ];

  return (
    <ImplantCenterLayout
      title="Cosmetic Dentistry Fort Worth TX | Smile Makeover | Fort Worth Dental Implant Center"
      description="Complete cosmetic dentistry services in Fort Worth TX. Teeth whitening, veneers, Invisalign, CEREC crowns, smile makeovers. Transform your smile. Call (817) 854-4329."
      keywords="cosmetic dentistry fort worth, smile makeover fort worth tx, teeth whitening, porcelain veneers, invisalign, CEREC crowns"
    >
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{backgroundImage: "url('/images/services/frame-1288-2.png')"}} />
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Cosmetic Dentistry at Fort Worth Dental
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Transform your smile with our comprehensive cosmetic dental services. From whitening to complete smile makeovers.
            </p>
            <a href="tel:8178544329" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition inline-block">
              Schedule Consultation: (817) 854-4329
            </a>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Your Smile, Your Confidence</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              At Fort Worth Dental, we believe everyone deserves a smile they're proud to show. Our cosmetic dentistry services combine artistry with advanced dental technology to create beautiful, natural-looking results.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Whether you want to brighten stained teeth, straighten crooked teeth, or completely transform your smile, our experienced cosmetic dentists will create a personalized treatment plan just for you.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">Our Cosmetic Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <img 
                  src={service.image} 
                  alt={`${service.title} cosmetic dentistry Fort Worth TX`}
                  className="w-full h-48 object-cover"
                  onError={(e) => { e.target.src = '/images/services/dentist-examines.jpg'; }}
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Why Choose Fort Worth Dental for Cosmetic Dentistry?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎨</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Artistic Approach</h3>
                  <p className="text-gray-600">Our dentists combine clinical expertise with an eye for aesthetics.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🔬</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Advanced Technology</h3>
                  <p className="text-gray-600">CEREC, digital imaging, and the latest cosmetic techniques.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⭐</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Proven Results</h3>
                  <p className="text-gray-600">30+ years of creating beautiful smiles in Fort Worth.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">💳</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Flexible Financing</h3>
                  <p className="text-gray-600">0% interest payment plans to make your dream smile affordable.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready for Your Smile Transformation?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Schedule a cosmetic consultation to discuss your goals and create your personalized treatment plan.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8178544329" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition">
              Call (817) 854-4329
            </a>
            <Link to="/implant-center#consultation-form" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-500 transition">
              Request Consultation
            </Link>
          </div>
        </div>
      </section>
    </ImplantCenterLayout>
  );
};

export default ImplantCosmeticDentistry;
